/* eslint-disable @typescript-eslint/no-explicit-any */
import { BulkPaymentMasterStatus } from '@finxone-platform/form-action';
import { ClientType } from './account-type.enum';
import {
  CreateCurrencyAccountTypeAttributesType,
  DownloadDocumentTypeKey,
  SumsubReviewStatus,
} from './common.enum';
import { LayoutNames } from './config.type';
import { UiZoneWidgetConfig } from './ui-system-config.v2.type';
import { TOP_LEVEL_ID } from './workflow-widget.utils';
import { APP_ZONES } from './workflow.type';
import { ConfirmationPreferenceTypes } from './workflow.v2.type';

export type BulkPaymentsSummary = {
  totalPayments?: number;
  totalDuplicatePayments?: number;
  totalFailedPayments?: number;
  totalSuccessPayments?: number;
  totalSchedulePayments?: number;
  totalUniqueBeneficiaries?: number;
  totalPendingPayments?: number;
  totalPendingBeneficiary?: number;
  totalPendingTransactions?: number;
  totalAmount?: number;
  currency?: string;
  accountName?: string;
  accountId?: string;
  masterStatus: BulkPaymentMasterStatus;
  executedAt?: Date;
  totalErrors?: number;
  recordId: string;
};

export type PaginatedResponse<Type> = {
  result: Type[];
  totalPages: number | undefined;
  page: number;
  limit: number;
  totalItems?: number;
  meta?: {
    bulkPaymentsSummary?: BulkPaymentsSummary;
  };
};

export type ExtendedPaginatedResponse<Type> = PaginatedResponse<Type> & {
  map<U>(mapFn: (item: Type, index: number, array: Type[]) => U): PaginatedResponse<U>;
};

// Factory function to create PaginatedResponse objects with map functionality
export function createPaginatedResponse<T>(data: {
  result: T[];
  totalPages: number | undefined;
  page: number;
  limit: number;
  totalItems?: number;
  meta?: { bulkPaymentsSummary?: BulkPaymentsSummary };
}): ExtendedPaginatedResponse<T> {
  const response = {
    ...data,
    map: function <U>(mapFn: (item: T, index: number, array: T[]) => U): PaginatedResponse<U> {
      return createPaginatedResponse({
        ...this,
        result: this.result.map(mapFn),
      });
    },
  };

  return response;
}

// Update the existing function to use the factory
export function mapPaginatedResponse<T, U>(
  paginatedResponse: PaginatedResponse<T>,
  mapFn: (item: T, index: number, array: T[]) => U,
): ExtendedPaginatedResponse<U> {
  return createPaginatedResponse({
    ...paginatedResponse,
    result: paginatedResponse.result.map(mapFn),
  });
}

export type ActiveOrgRole = {
  organisation: string;
  role: string;
};

export type OrgRoles = {
  id: string;
  name: string;
};

export type OrgAttributes = {
  county?: string[];
  country?: string[];
  street?: string[];
  city?: string[];
  state?: string[];
  address1?: string[];
  address2?: string[];
  address3?: string[];
  address4?: string[];
  postalCode?: string[];
  feeGroupId?: string[];
  flatName?: string[];
  propertyName?: string[];
  propertyNumber?: string[];
  incorporationDate?: string[];
  identificationNumber?: string[];
  phoneNumber?: string[];
  type?: string[];
};

export interface OrgTypes<T extends Record<string, string[]> = OrgAttributes> {
  id: string;
  name: string;
  displayName: string;
  url: string;
  realm: string;
  domains: string[];
  attributes: T;
}

export type OrganisationAddressDetails = {
  street: string;
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
};
export type OrgInfoDetails = {
  id: string;
  name: string;
  address: OrganisationAddressDetails;
  identificationNumber: string;
  incorporationDate: string;
};

export type Organisation = {
  id: string;
  name: string;
  displayName: string;
  realm: string;
  primaryContact?: OrgPrimaryContact;
};

export type OrgPrimaryContact = Partial<{
  name: string;
  email: string;
  phoneNumber: string;
}>;

export interface Address {
  address1: string;
  address2?: string;
  address3?: string;
  address4?: string;
  city: string;
  county?: string;
  state?: string;
  postalCode: string;
  country: string;
  flatName?: string;
  propertyName?: string;
  propertyNumber?: string;
  street?: string;
}

export interface PreviousAddress extends Address {
  fromDate: string;
  toDate?: string;
}

export interface UserMarketingPreference {
  email: boolean;
  sms: boolean;
  push: boolean;
  mail: boolean;
}

export interface UserProfileData extends Address {
  id?: string;
  enabled?: boolean;
  role?: string;
  organisation?: string;
  orgList?: Array<OrganisationList>;
  firstName: string;
  lastName: string;
  middleName?: string;
  fullName?: string;
  logo?: string;
  email: string;
  emailVerified?: boolean;
  dob: string;
  countryOfBirth?: string;
  phoneNumber: string;
  phoneVerified?: boolean | string;
  phoneCode?: string;
  custom_questions?: customQuestions;
  userName?: string;
  marketingPreferences?: UserMarketingPreference;
  profileUpdatePermittedDate?: string;
  previousAddresses?: PreviousAddress[];
  extraAttributes?: {
    [key: string]: string | undefined;
  };
}

export type KybCompanyInfo = {
  companyName: string;
  country: string;
  identificationNumber: string;
  incorporationDate: string;
};

export const defaultUserProfileData = {
  firstName: '',
  lastName: '',
  middleName: '',
  email: '',
  dob: '06/01/2000',
  country: '',
  phoneNumber: '',
  address1: '',
  address2: '',
  address3: '',
  address4: '',
  city: '',
  county: '',
  postalCode: '',
  phoneCode: '',
} as UserProfileData;

export type FileUploadResponse = {
  message: string;
  path: string;
  success: boolean;
  id: string;
  filename: string;
  mimeType: string;
};

export enum FileGlobalStatusFlag {
  ACTIVE = 1,
  INACTIVE = 0,
}

export enum ProviderClientType {
  CORPORATE = 'corporate',
  PERSONAL = 'personal',
}

export enum OnboardingConfigFlowTypes {
  BUSINESS = 'business-first',
  PERSONAL = 'personal-first',
}

export enum OrgCheckTypes {
  NAME = 'name',
  identificationNumber = 'identification_number',
}

export type FilesListResponse = {
  id: string;
  path: string;
  filename: string;
  global_status: FileGlobalStatusFlag;
  created_at: Date;
  category: string;
};

export type customQuestions = {
  [key: string]: string;
};

export type OrganisationList = {
  id: string;
  roles: Array<string>;
  name: string;
  kybVerificationStatus?: KybStatus;
  createdDate: string;
  type?: ClientType;
  attributes?: Record<string, string | boolean>;
};

export type OrganisationDetail = {
  id: string;
  kybVerificationStatus: string;
};

export interface UserInfoType extends Address {
  id?: string;
  role?: string;
  organisation?: string;
  orgList?: Array<OrganisationList>;
  orgCounter?: number;
  fullName?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  logo?: string;
  email: string;
  emailVerified?: boolean;
  userName?: string;
  dob: string;
  country: string;
  phoneNumber: string;
  phoneVerified?: boolean | string;
  phoneCode?: string;
  custom_questions?: customQuestions;
  kycVerificationStatus?: string;
  profileCompletionStatus?: string;
  organisationKybStatus?: string;
  settings?: string;
  extraAttributes?: {
    [key: string]: string | undefined;
  };
  permissions?: string[];
  theme?: string;
  marketingPreferences?: UserMarketingPreference;
  profileUpdatePermittedDate?: string;
  profileCompletion?: boolean;
  applicationReference?: string;
}

export type OnboardUserResponse = {
  id: string;
  persona: string;
  status: KycStatus;
  accessToken?: string;
  refreshToken?: string;
};

export type ResponseWithAlertCodesType<T> = {
  data: T;
  alertCodes?: string[];
};

export const customQuestionPrefix = 'question_name_';

export enum RequiredActions {
  NO_ACTION = '',
  VERIFY_EMAIL = 'VERIFY_EMAIL',
  UPDATE_PASSWORD = 'UPDATE_PASSWORD',
  TERMS_AND_CONDITIONS = 'TERMS_AND_CONDITIONS',
  UPDATE_PROFILE = 'UPDATE_PROFILE',
  CONFIGURE_TOTP = 'CONFIGURE_TOTP',
}

export enum KycStatus {
  STARTED = 'started',
  SUCCESS = 'success',
  FAILED = 'failed',
  PENDING = 'pending',
  IN_PROGRESS = 'in-progress',
  KYC_PENDING = 'kyc-pending',
  NOT_STARTED = 'not-started',
  CANCELLED = 'cancelled',
}

export enum KybStatus {
  STARTED = 'started',
  SUCCESS = 'success',
  FAILED = 'failed',
  PENDING = 'pending',
  IN_PROGRESS = 'in-progress',
  KYB_PENDING = 'kyb-pending',
  REVIEW_PENDING = 'review-pending',
}

export enum VerificationAction {
  RE_CHECK = 'recheck',
  HOLD = 'hold',
  PENDING = 'pending',
}

export enum ButtonActionType {
  KYC_EMAIL_PHONE_VERIFIED_CHECK = 'check-email-phone-verified',
  UPDATE_ORGANISATION = 'update-organisation',
  PAY_NEW_INTL_BENEFICIARY = 'pay-new-intl-beneficiary',
  REMOVE_BENEFICIARY = 'remove-beneficiary',
  REDIRECT_TO_EDIT_BENEFICIARY = 'redirect-to-edit-beneficiary',
  UPDATE_BENEFICIARY_DETAILS = 'update-edit-beneficiary',
  EDIT_BENEFICIARY = 'edit-beneficiary',
  EDIT_BENEFICIARY_UNIFIED = 'edit-beneficiary-unified',
  DOWNLOAD_CSV = 'download-csv',
  PAYMENT_REQUEST = 'payment-request',
  PAYMENT_REQUEST_PROCESSING = 'payment-request-processing',
  PAYMENT_REQUEST_APPROVE = 'payment-request-approve',
  PAYMENT_REQUEST_REJECT = 'payment-request-reject',
  PAYMENT_APPROVER_MAKE_PAYMENT = 'payment-approver-init-payment',
  BULK_PAYMENT_DOWNLOAD_UPLOAD_PROCESSING = 'payment-payment-download-upload-processing',
  REFRESH_BULK_PAYMENT_LIST = 'refresh-bulk-payment-list',
  BULK_PAYMENT_ADD_BENEFICIARY = 'bulk-payment-add-beneficiary',
  BULK_PAYMENT_BENEFICIARY_VALIDATION = 'bulk-payment-beneficiary-validation',
  BULK_PAYMENT_MAKE_PAYMENT = 'bulk-payment-make-payment',
  REVIEW_INFORMATION_VERIFICATION_PERSONAL = 'review-information-verification-personal',
  REVIEW_INFORMATION_VERIFICATION_BUSINESS = 'review-information-verification-business',
  CHECK_SMS_AUTH_STATUS_AND_REDIRECT = 'check-sms-auth-status-and-redirect',
  INIT_INTL_PAYMENT = 'init-intl-payment',
  SAVE_SPENDING_BUDGET = 'save-spending-budget',

  CLEAR_FORM_STATE_AND_REDIRECT_TO_EDIT_PROFILE = 'clear-form-state-and-redirect-to-edit-profile-page',
  REDIRECT_BUSINESS_USER_TO_LOGIN = 'redirect-business-user-to-login',
  VERIFICATION_EDIT_INFORMATION = 'verification-edit-information',
  SUBMIT_USER_PROFILE_UPDATE = 'submit-user-profile-update',
  SUBMIT_PROFILE_CHANGES_IN_VERIFICATION_FLOW = 'submit-profile-changes-in-verification-flow',
  SUBMIT_PROFILE_CHANGES_IN_PROFILE_ZONE = 'submit-profile-changes-in-profile-zone',
  BULK_PAYMENT_AUTHENTICATION_LINK = 'bulk-payment-authentication-link',
  BULK_PAYMENT_CANCEL = 'bulk-payment-cancel',
  BULK_PAYMENT_REJECT = 'bulk-payment-reject',
  BULK_PAYMENT_APPROVER = 'bulk-payment-approver',
  SECURE_CVV_DETAILS = 'secure-cvv-details',
  ADD_BENEFICIARY = 'add-beneficiary',
  USE_SUGGESTED_NAME_COP = 'use-suggested-name-cop',
  SHOW_PIN = 'show-pin',
  SHOW_EPIN = 'show-e-pin',
  CANCEL_CARD = 'cancel-card',
  SET_CARD_NAME = 'set-card-name',
  REFRESH_CARDS_AND_REDIRECT = 'refresh-cards-and-redirect',
  GET_SENSITIVE_CARD_DETAILS = 'get-sensitive-card-details',
  SEPA_TRANSFER_PAYMENT_FLOW = 'sepa-transfer-payment-flow',
  SKIP_BENEFICIARY_ADDRESS = 'skip-beneficiary-address',
  TOGGLE_BIOMETRIC_AUTHENTICATION = 'toggle-biometric-authentication',
  EXCHANGE_RE_QUOTE = 'exchange-re-quote',
  EXCHANGE_ACCEPT_QUOTE = 'exchange-accept-quote',
  INTL_TRANSFER_PRECHECK = 'intl-transfer-precheck',
  CHANGE_ACCOUNT_NICKNAME = 'change-account-nickname',
  MARK_ALL_NOTIFICATIONS_AS_READ = 'mark-all-notifications-as-read',
  UPDATE_MARKETING_PREFERENCES = 'update-marketing-preferences',

  // Action Button Widget New Actions
  INITIATE_PAYMENT_SUMMARY = 'initiate-payment-summary',
  SUBMIT_PAYMENT = 'submit-payment',
  DEACTIVATE_LEDGER_ACCOUNT = 'deactivate-ledger-account',
  DELETE_DOCUMENT = 'delete-document',
  UPLOAD_DOCUMENT = 'upload-document',
  VIEW_DOCUMENT = 'view-document',
  DOWNLOAD_DOCUMENT = 'download-document',
  REPLACE_DOCUMENT = 'replace-document',
  SECURE_DOCUMENT_UPLOAD = 'secure-document-upload',
  MARK_FAVORITE_BENEFICIARY = 'mark-favorite-beneficiary',
  FEEDBACK_COUNTER = 'feedback-counter',

  // Beneficiary Form Actions
  BENEFICIARY_FORM_BANK_PROCEED = 'beneficiary-form-bank-proceed',
  BENEFICIARY_FORM_ADDRESS_PROCEED = 'beneficiary-form-address-proceed',
  BENEFICIARY_FORM_SUBMIT = 'beneficiary-form-submit',
  REMOVE_FAVORITE_BENEFICIARY = 'remove-favorite-beneficiary',
  BENEFICIARY_FORM_COP = 'beneficiary-form-cop',
  CANCEL_SCHEDULED_PAYMENT = 'cancel-scheduled-payment',
  CANCEL_PENDING_WITHDRAWAL = 'cancel-pending-withdrawal',

  //Onboarding Actions
  ONBOARDING_SUBMIT = 'onboarding-submit',
  ONBOARDING_BANK_DETAILS_VERIFICATION = 'onboarding-bank-details-verification',

  // Savings Account Actions
  PROCESS_NOMINATED_ACCOUNT = 'process-nominated-account',
  CHECK_PAYEE_FOR_NOMINATED_BANK_ACCOUNT = 'check-payee-for-nominated-bank-account',
  UPDATE_NOMINATED_BANK_ACCOUNT = 'update-nominated-bank-account',
  PISP_DEPOSIT = 'pisp-deposit',
  PISP_CANCEL_RECURRING_PAYMENT = 'pisp-cancel-recurring-payment',
  PISP_REVIEW = 'pisp-review',
  // DEV/QA/UAT test aid only - see savings-account-actions/dev-test-clear-pisp-institution.utils.ts.
  // No-ops outside GoCardless sandbox. Never rename: persisted into workflow_config JSONB.
  DEV_TEST_CLEAR_PISP_INSTITUTION = 'dev-test-clear-pisp-institution',
  WITHDRAW_FROM_SAVINGS_ACCOUNT = 'withdraw-from-savings-account',
  INITIATE_SAVINGS_INTERNAL_TRANSFER = 'initiate-savings-internal-transfer',
  OPEN_NEW_SAVINGS_ACCOUNT = 'open-new-savings-account',
  REVIEW_ACCOUNT_REQUESTS = 'review-account-requests',
  MATURITY_DECISION_RENEW = 'maturity-decision-renew',
  MATURITY_DECISION_WITHDRAW = 'maturity-decision-withdraw',
  MATURITY_DECISION_PARTIAL_REINVEST = 'maturity-decision-partial-reinvest',
  INVITE_JOINT_ACCOUNT = 'invite-joint-account',
  ACCEPT_JOINT_ACCOUNT_INVITATION = 'accept-joint-account-invitation',
  DECLINE_JOINT_ACCOUNT_INVITATION = 'decline-joint-account-invitation',

  // Misc utility actions
  SCRIPTED_ACTION = 'scripted-action',
  CLEAR_FORM_STATE_AND_REDIRECT = 'clear-form-state-and-redirect',
  CLOSE_SAVINGS_ACCOUNT = 'close-savings-account',
  START_A_NEW_SUPPORT_CHAT = 'start-a-new-support-chat',
  SAVE_TRANSACTION_NOTE = 'save-transaction-note',
  EDIT_TRANSACTION_NOTE = 'edit-transaction-note',

  // card actions
  ADD_CARD = 'add-card',
  ACTIVATE_CARD = 'activate-card',

  // Business onboarding - director wizard actions
  ADD_DIRECTOR = 'add-director',
  SAVE_DIRECTOR = 'save-director',
  REMOVE_DIRECTOR = 'remove-director',
  SAVE_PRIMARY_DIRECTOR_INFO = 'save-primary-director-info',
  BUSINESS_ONBOARDING_SUBMIT = 'business-onboarding-submit',
}

export enum ProfileCompletionStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
}

export type UserAttributes = {
  id: string;
  organisationId: string;
  role: string;
  extra_attributes: any;
};

export type UsersList = {
  limit: number;
  page: number;
  result: UsersResultList[];
  totalPages: number;
  totalItems: number;
  totalRecords: number;
};
export type AssignPersona = {
  email: string;
  persona: string;
  roles: string[];
};
export interface AssociateUserPersonaResponse {
  userExist: boolean;
  userId: string;
  persona: string;
}

export type UsersResultList = {
  id: string;
  organisationId: string;
  role: string;
  extra_attributes: any;
  kycVerificationStatus?: KycStatus;
  profileCompletionStatus?: ProfileCompletionStatus;
  activeOrganisationKybStatus?: string;
  orgCounter?: number;
  logo?: string;
  events?: string;
  email?: string;
  guest_type?: string;
  isPrimary?: string;
};

export type UsersAttribute = {
  dob: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  phoneNumber: string;
  projectId: string;
  roleId: string;
  userName: string;
  country_code?: string;
  country?: string;
};

export type SearchApiParams = {
  page: number;
  size: number;
  query: string;
  sortKey: string;
};

export interface AutoSuggestAddressResponse {
  address: string;
  id: string;
}

export type AutoSuggestAddressResponseItems = Array<AutoSuggestAddressResponse>;

export type Roles = {
  name: string;
  slug: string;
};

export type CountryOption = {
  name: string;
  dial_code: string;
  code: string;
};

export type ImportUsers = {
  address: string;
  phone: string;
  email: string;
  last_name: string;
  first_name: string;
};

// TODO: remove duplicate address properties
export interface UserPayload extends Address {
  dob?: string;
  email: string;
  firstName: string;
  lastName: string;
  password?: string;
  phoneNumber: string;
  projectId?: string;
  userName?: string;
  street_adress?: string;
  post_code?: string;
  middleName?: string;
  extraAttributes?: any;
  roleId?: string;
  profileStatus?: boolean;
  phoneVerified?: string | boolean;

  //keys from Add User Flow
  organisationId?: string;
  profilePicture?: string;
  additionalRoles?: string;
  importUser?: boolean;
  emailVerified?: string | boolean;
}

export type ImportUsersPayload = {
  size: number;
  page: number;
  last_name: string;
  first_name: string;
};

export type LogosTypeList = {
  limit: number;
  page: number;
  totalPages: number;
  result: LogosResultTypeList[];
};

export type LogosResultTypeList = {
  filename: string;
  path: string;
  id: string;
  global_status: boolean;
  created_at?: Date;
  category?: string;
  isActive?: boolean;
  isDisabled?: boolean;
  isSVGFile?: boolean;
};

export interface ProjectIcon extends LogosResultTypeList {
  iconName: string;
  isRegisteredIcon: boolean;
  iconNamespace: string;
}

export type LogodeletePayload = {
  id: string[];
  projectId: string;
};

export interface ProfileStateModel extends Address {
  id?: string;
  roleId?: string;
  orgId?: string;
  orgList?: Array<OrganisationList>;
  fullName: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  logo: string;
  email: string;
  emailVerified?: boolean;
  userName: string;
  dob: string;
  phoneNumber: string;
  phoneVerified?: boolean;
  phoneCode?: string;
  custom_questions?: customQuestions;
  activeOrganisationKybStatus?: string;
  activeOrganisationId?: string;
  activeRole?: string;
  settings?: string;
  kycVerificationStatus: string;
  kybVerificationStatus?: string;
  profileCompletionStatus: string;
  orgCounter?: number;
  availableConfirmationMethods?: ConfirmationPreferenceTypes[];
  extraAttributes?: {
    [key: string]: string | undefined;
  };
  activeTheme?: string;
  marketingPreferences?: UserMarketingPreference;
  applicationReference?: string;
  profileUpdatePermittedDate?: string | undefined;
}

export abstract class ProviderClass<T> {
  widgetConfig: UiZoneWidgetConfig;
  attri: T;
  uniqueId: number;
  parentComponent?: string;
  allwidgets?: [];
  userProfileData?: any;
  userProfile: ProfileStateModel;
  id?: string;
}

export type SearchUsersPayload = {
  email: string;
  last_name: string;
  first_name: string;
};

export interface IAutoTokenResponse {
  token: string;
}

export type KycTokenResponse = {
  token: string;
  userId: string;
  email: string;
  phoneNumber: string;
};

export type KycCheckStatus = { status: SumsubReviewStatus };

export type GenerateOtpPayload = {
  fieldType: 'email' | 'phone';
  fieldValue: {
    [key: string]: string;
  };
  verificationCode?: string;
  /**
   * Present only for signed-in flows (profile update, payment SCA). Absent during onboarding, which
   * is why onboarding's verification has to be bound by a returned token instead of by user id.
   */
  userId?: string;
  /**
   * The form slice the verification token and verifiedAt markers should be written into.
   * Onboarding flows that share a contact channel but use a different form (e.g. business vs
   * individual) use this to keep the token in the same slice the submit CTA will read.
   */
  formId?: string;
};

export type VerificationFlag = {
  fieldName: string;
  isVerified: boolean;
};

export interface ProgressBarWidgetModel {
  uniqueId?: string | number;
}

export enum ViewType {
  LIST = 'list',
  TABLE = 'table',
  DROPDOWN = 'dropdown',
}

export enum Orientation {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

/**
 * Canvas-only preview state, persisted at widgetProperties.appBuilderState. These slugs are the
 * contract between the shared dropdown (WidgetStyleConst.showAppBuilderPreviewState) and the
 * runtime widgets that read the key behind their app-builder guard.
 */
export enum AppBuilderPreviewState {
  DEFAULT = 'default',
  NO_RECORDS = 'noRecords',
  FETCHING = 'fetching',
}

export enum BeneficiaryDataContext {
  ALL_BENEFICIARIES = 'all_beneficiaries',
  CURRENT_ACCOUNT_CONTEXT = 'current_account_context',
  BENEFICIARY_CURRENCY_CONTEXT = 'beneficiary_currency_context',
}

export type AllTransactionType = TransactionType | CurrencyExchangeTransactionType;

export enum TransactionType {
  INCOMING = 'incoming',
  OUTGOING = 'outgoing',
  ALL = 'all',
  SCHEDULED = 'scheduled',
  PENDING_WITHDRAWAL = 'pending-withdrawal',
}

export enum TransactionCategory {
  CREDIT = 'credit',
  DEBIT = 'debit',
}

export enum CurrencyExchangeTransactionType {
  CURRENCYINCOMING = 'currencyIncoming',
  CURRENCYOUTGOING = 'currencyOutgoing',
  CURRENCYALL = 'currencyAll',
}

export enum CardTransactionType {
  CARD_TRANSACTION_ALL = 'cardTransactionAll',
}

export enum TransactionStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  RELEASED = 'released',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  FAILED = 'failed',
  ON_HOLD = 'on-hold',
}

export enum ClientCompanyType {
  SOLE_TRADER = 'soleTrader',
  PARTNERSHIP = 'partnership',
  PRIVATE = 'private',
  NON_PROFIT = 'nonProfit',
  COMMUNITY_INTEREST = 'communityInterest',
  PUBLIC = 'public',
  INTERNATIONAL_NGO = 'internationalNGO',
}

export interface OrganisationOnboardingRequest extends Address {
  name: string;
  userId?: string;
  extraAttributes: any;
}

export type CustomQuestionRequest = {
  name: string;
  type: string;
  label: string;
  value: unknown;
};

export type KeycloakOrgMember = {
  createdTimestamp: number;
  email: string;
  emailVerified: boolean;
  enabled: boolean;
  firstName: string;
  id: string;
  lastName: string;
  username: string;
  organizations: object;
};

export type KeycloakUserInfo = {
  createdTimestamp: any;
  email: string;
  emailVerified: boolean;
  enabled: boolean;
  firstName: string;
  id: string;
  lastName: string;
  username: string;
  actionEnabled?: boolean;
  groups: string[];
  attributes: any;
};

export type KeycloakUser = {
  id: string;
  createdTimestamp: number;
  username: string;
  enabled: boolean;
  totp: boolean;
  emailVerified: boolean;
  firstName: string;
  lastName: string;
  email: string;
  attributes: {
    [key: string]: [string];
  };
  disableableCredentialTypes: string[];
  requiredActions: string[];
  notBefore: number;
};

export type NotificationChannelSetting = {
  sms: boolean;
  email: boolean;
  push: boolean;
  pushNative: boolean;
};

export type NotificationSettings = {
  [tagOrCategory: string]: NotificationChannelSetting;
};
export interface NotificationSettingsPayload {
  dailyBalance: NotificationChannelSetting;
  transactionAlerts: NotificationChannelSetting;
  addBeneficiaryAlerts: NotificationChannelSetting;
  paymentsRequestAlerts: NotificationChannelSetting;
}

export type AddCustomQuestionAnswerRequest = {
  customQuestionUniqueId: string;
  value: string;
  checkboxValues?: string[];
};

export type ClientAddress = {
  houseNumber?: string;
  street: string;
  city: string;
  state?: string;
  postalCode: string;
  country: string;
};

export type CreateClientRequest = {
  client?: {
    legalName: string;
    country: string;
    identificationNumber: string;
    incorporationDate?: string;
  };
  profile: {
    phoneNumber: string;
    email: string;
    dob?: string;
    country: string;
    residentialAddress?: ClientAddress;
    firstName: string;
    lastName: string;
  };
  address: ClientAddress;
  organisationId: string;
  userId?: string;
  clientType: ProviderClientType;
  metadata?: any;
  attributes?: any;
  provider?: string;
};

export type ImportClientAddress = {
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  city: string;
  state?: string;
  postalCode: string;
  country: string;
};
export type ImportCreateClientRequest = {
  client?: {
    legalName: string;
    country: string;
    identificationNumber: string;
    incorporationDate?: string;
    companyType?: ClientCompanyType;
    address: ImportClientAddress;
  };
  profile: {
    phoneNumber: string;
    email: string;
    dob?: string;
    country: string;
    residentialAddress?: ImportClientAddress;
    firstName: string;
    lastName: string;
    isPep?: boolean;
    expectedVolumeType?: string;
    expectedVolumeCurrency?: string;
    expectedVolumeRange?: string;
    riskScore?: string;
    accountUsage?: string[];
  };
  organisationId: string;
  userId?: string;
  clientType: ProviderClientType;
  metadata?: any;
};

export type CreateClientResponse = {
  id: string;
};
export type CreateAssociatedUserResponse = {
  id: string;
  isRemoved: boolean;
};

export type ExistedClientResponse = {
  id: string;
  clientExist: boolean;
  createdCustomers: string[];
};

export type VerificationOrganisationData = {
  id: string;
  name: string;
  address: {
    country: string;
    state: string;
    city: string;
    street: string;
    address1: string;
    address2: string;
    address3: string;
    address4: string;
    postalCode: string;
  };
  identificationNumber: string;
  incorporationDate: string;
};

export interface OrganisationVerificationRequest extends Address {
  name: string;
  extraAttributes: any;
}

export type OrgUserInfo = {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  city: string;
  dob: string;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  position?: string;
  persona?: string;
};

export type OrgUserPaginateResponse = {
  result: OrgUserInfo[];
  totalPages: number;
  totalRecords: number;
  page: number;
  limit: number;
};

//TODO: update to extend Address interface
export interface AddEmployeeRequestType {
  email: string;
  emailVerified?: boolean;
  phoneNumber: string;
  phoneVerified?: boolean;
  country?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  address4?: string;
  flatName?: string;
  propertyName?: string;
  propertyNumber?: string;
  city?: string;
  county?: string;
  postalCode?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  userName: string;
  roleId?: string;
  dob?: string;
  password: string;
  organisationId: string;
  external?: boolean;
  importUser?: boolean;
  additionalRoles?: string[];
  profilePicture?: string;
  logo?: string;
  extraAttributes?: { [key: string]: string };
}

export interface InviteEmployeeType {
  email: string;
  orgId: string;
  persona: string;
  roles: string[];
}

export type ZoneActionOptions = {
  label: string;
  value: string;
  isIdNotExist: string;
  zoneToNavigate?: APP_ZONES;
  urlToNavigate?: string;
};

export type FileType = {
  fileType: string;
  filename: string;
  id: string;
  path: string;
};

export type InviteGuestRequestType = {
  email: string;
  name: string;
  type: string;
  typeId: string;
};

export type InviteGuestResponseType = {
  userExist: string;
  userId: string;
};

export type EmployeeTripType = {
  id: string;
  user_id: string;
  trip_id?: string;
  trip: {
    start: string;
    name: string;
    id: string;
    end: string;
    embarkLocation?: string | null;
    disembarkLocation?: string | null;
    embarkLocation_2?: string | null;
    disembarkLocation_2?: string | null;
    facility?: {
      attributes?: string;
      id: string;
      is_mobile?: boolean;
      name: string;
    };
  };
};

export type ViewUserAssignPersona = {
  id: string;
  roleId: string;
  additionalRoles: string[];
};

export type BulkPaymentResultList = {
  id: string;
  row?: string;
  name: string;
  beneficiaryId: string | null;
  beneficiaryStatus: string;
  beneficiaryExistStatus: string;
  amount: string;
  currency: string;
  scheduleType: string;
  scheduleDate: string;
  transactionId: string | null;
  transactionReference: string | null;
  transactionPaymentReason: string | null;
  transactionStatus: string;
  status: string;
  created_at: string;
  executed_at: string;
  updated_at: string;
  beneficiaryBankAccounts: [
    {
      country: string;
      currency: string;
      account_number: string;
      sort_code: string;
      iban: string | null;
      bic: string | null;
      aba: string | null;
      ach: string | null;
      rtn_canada: string | null;
      clabe: string | null;
      bsb_code: string | null;
      ifsc: string | null;
      cnaps: string | null;
      bank_code: string | null;
      branch_code: string | null;
      created_at: string;
      updated_at: string;
      id: string;
      address: {
        address_line_1: string | null;
        address_line_2: string | null;
        address_line_3: string | null;
        city: string | null;
        region: string;
        postal_code: string;
        country: string;
        house_number: string | null;
        created_at: string;
        updated_at: string;
        id: string;
      };
    },
  ];
  isDuplicate?: boolean;
  isError?: boolean;
  entityStyle?: { 'background-color': string } | '';
  actionEnabled?: boolean;
  beneficiaryCoPStatus?: string;
  beneficiaryCoPCode?: string;
  beneficiaryExistAndCopStatus?: string;
  type?: string;
  beneficiaryCoPName?: string;
};

export type InternalOnboard = {
  type: ClientType;
  payload: InternalOnboardingIndividual | InternalOnboardingCorporate;
};

export type InternalOnboardingIndividual = {
  firstName: string;
  middleName?: string;
  lastName: string;
  country: string;
  kycStatus: KycStatus;
  rolesToAssign: string[];
  address: {
    address1: string;
    address2?: string;
    address3?: string;
    address4?: string;
    city: string;
    county?: string;
    country: string;
    postalCode: string;
  };
  phoneNumber: string;
  dob: string;
  email: string;
  userName?: string;
  attributes?: {
    [key: string]: string;
  };
};

export type InternalOnboardingCorporate = {
  legalName: string;
  incorporationDate: Date;
  country: string;
  kybStatus: KybStatus;
  address: {
    address1: string;
    address2?: string;
    address3?: string;
    address4?: string;
    city: string;
    county?: string;
    country: string;
    postalCode: string;
  };
  attributes?: {
    [key: string]: string;
  };
  user: {
    rolesToAssign: string[];
    firstName: string;
    middleName?: string;
    lastName: string;
    address: {
      address1: string;
      address2?: string;
      address3?: string;
      address4?: string;
      city: string;
      county?: string;
      country: string;
      postalCode: string;
    };
    phoneNumber: string;
    dob: string;
    email: string;
    userName?: string;
  };
};

export enum DepositDetailsType {
  BALANCE = 'balance',
  INTEREST = 'interest',
  DEPOSIT_DETAILS = 'deposit-details',
  BALANCE_WITH_INTEREST = 'balance-with-interest',
}

export type DepositInfo = {
  depositAmount?: number;
  initialFundAmount?: number;
  pendingInitialFundAmount?: number;
  fee?: number;
  feeRate?: number;
  currency?: string;
  totalUnit?: number;
  initialExchangeRate?: number;
  currentExchangeRate?: number;
  currentExchangeRateCreatedDate?: string;
  fundGrowthPercentage?: number;
  fundGrowthValue?: number;
  lastInterestCredit?: {
    date: string;
    amount: number;
    currency: string;
  };
  totalBalance?: {
    currency: string;
    amount: number;
  };
  totalInterest?: {
    currency: string;
    amount: number;
    incrementPercentage: number;
  };
  totalBalanceWithInterest?: {
    currency: string;
    amount: number;
  };
  depositType?: CreateCurrencyAccountTypeAttributesType;
};

export type TermDepositAccountDetailsType = DepositInfo;

// Define allowed operators
export type MathOperator =
  '+' | 'add' | '-' | 'subtract' | '*' | 'multiply' | '/' | 'divide' | 'quotient' | '%' | 'remainder';

export interface NotificationWorkflowSetting {
  id: string; // UUID
  name: string; // Human-readable name
  identifier: string; // Novu identifier
  identifier_workflow_id: string; // Novu identifier
  tenant?: string | null; // Novu tenant; null/undefined means the default NOVU_TENANT
  version: number; // Workflow version (1 = v1/legacy, 2 = v2/new API)
  is_sms_enabled: boolean;
  is_email_enabled: boolean;
  is_in_app_enabled: boolean;
  is_push_enabled: boolean;
  is_active: boolean;
  created_at: string | Date; // timestamp
  updated_at: string | Date; // timestamp
}

export type NotificationWorkflowSettingResponse = {
  result: NotificationWorkflowList[];
  totalItems: number;
  totalPages: number;
  page: number;
  limit: number;
};

export interface NotificationWorkflow {
  id: string;
  name: string;
  identifier_v1: string;
  identifier_v2: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface NotificationWorkflowResponse {
  data: NotificationWorkflow[];
}

export interface NotificationWorkflowList {
  id: string;
  project_id: string;
  workflow_id: string;
  // Novu tenant key (e.g. 'gfc'); null/undefined means the default NOVU_TENANT
  tenant?: string | null;
  version: number;
  is_sms_enabled: boolean;
  is_email_enabled: boolean;
  is_in_app_enabled: boolean;
  is_push_enabled: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  workflow: NotificationWorkflow;
}

export interface NotificationWorkflowRequest {
  id?: string;
  project_id: string;
  workflow_id: string;
  version: number;
  is_email_enabled: boolean;
  is_in_app_enabled: boolean;
  is_push_enabled: boolean;
  is_sms_enabled: boolean;
  is_active: boolean;
  // Novu tenant key (e.g. 'gfc'); omitted means the default NOVU_TENANT
  tenant?: string;
}

export type JsonSchemaProperty = {
  type?: string | string[];
  description?: string;
  properties?: Record<string, JsonSchemaProperty>;
  required?: string[];
  additionalProperties?: boolean;
  [key: string]: unknown;
};

export type NotificationWorkflowStepVariables = {
  type: 'object';
  properties: Record<string, JsonSchemaProperty>;
  additionalProperties?: boolean;
};

export interface NotificationWorkflowStep {
  name?: string;
  stepId?: string;
  type?: string;
  origin?: string;
  controls?: Record<string, unknown>;
  controlValues?: Record<string, unknown>;
  variables: NotificationWorkflowStepVariables;
  issues?: Record<string, unknown>;
}

export interface NotificationWorkflowChannels {
  sms: boolean;
  email: boolean;
  inApp: boolean;
  push: boolean;
}

export type NotificationWorkflowChannelType = keyof NotificationWorkflowChannels;

export interface NotificationWorkflowDetail {
  id: string;
  name: string;
  isActive: boolean;
  tenant: string | null;
  version: number | null;
  project_workflow_id?: string;
  channels: NotificationWorkflowChannels;
  createdAt: string | Date | null;
  updatedAt: string | Date | null;
  steps: NotificationWorkflowStep[] | null;
  payloadExample?: Record<string, unknown> | null;
  payloadSchema: { properties: Record<string, { type: string; description: string }>; type: string };
  subscriberExample: {
    email: string;
    phone: string;
    firstName: string;
    lastName: string;
  };
}

export type FormProgressInfo = {
  formSessionId: string;
  formData: any;
  deviceInfo: any;
  isCompleted: boolean;
  createdAt: string;
  updatedAt: string;
};

export type NotificationItem = {
  id: string;
  payload: {
    [key: string]: string;
  };
};

export type ThisbankUpdateCustomerPayload = {
  userId: string;
  organisationId: string;
  marketingPreferences?: UserMarketingPreference;
  profile?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    address?: {
      addressLine1: string;
      addressLine2: string;
      addressLine3: string;
      city: string;
      postcode: string;
    };
  };
};

export type UpdatedDocumentTemplateRequest = {
  templateKey: DownloadDocumentTypeKey;
  templateContent: string;
  projectId: string;
};

export type FormWidgetPlacementContext = {
  targetParentId: string | number | typeof TOP_LEVEL_ID;
  targetIndex?: number;
  target2dChildComponentIndex?: number;
  widgetLayoutType?: LayoutNames;
  zone?: string;
  pageIndex?: number;
};
